import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ColorSwatch } from '@/components/ui';
import { filterShades, type Shade } from '../../lib/shade-library';

const PAGE = 100;
const MORE = 200;

interface ShadeSearchProps {
  label: string;
  shades: Shade[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPick: (shade: Shade) => void;
}

/** Search box with a dropdown of matching shades from the library. */
export function ShadeSearch({ label, shades, open, onOpen, onClose, onPick }: ShadeSearchProps) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const matches = useMemo(() => filterShades(shades, query), [shades, query]);
  const visible = matches.slice(0, limit);

  const updateQuery = (value: string) => {
    setQuery(value);
    setLimit(PAGE);
  };

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <Search className="w-3.5 h-3.5 text-blue-600 absolute left-3 top-2.5 pointer-events-none" />
        <input
          type="text"
          value={query}
          aria-label={`Search ${label} shade`}
          onFocus={(e) => {
            setLimit(PAGE);
            onOpen();
            e.target.select();
          }}
          onChange={(e) => {
            updateQuery(e.target.value);
            onOpen();
          }}
          placeholder={`Search ${label} shade from ${shades.length.toLocaleString()} Excel shades (e.g. Abacadabra, NP N 2034 P)...`}
          className="w-full bg-white border border-slate-300 rounded-lg pl-8 pr-8 py-1.5 text-xs font-medium text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 shadow-2xs placeholder:text-slate-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              updateQuery('');
              onOpen();
            }}
            className="absolute right-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold px-1"
            title="Clear search filter"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div
          role="listbox"
          aria-label={`${label} shades`}
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-blue-200 rounded-xl shadow-xl max-h-64 overflow-y-auto p-1.5 space-y-1 custom-scrollbar"
        >
          <div className="flex items-center justify-between px-2.5 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 bg-slate-50/80 rounded-t-lg">
            <span>
              Select Shade for {label} ({matches.length.toLocaleString()} total shades)
            </span>
            <div className="flex items-center gap-2">
              {query && (
                <button
                  type="button"
                  onClick={() => updateQuery('')}
                  className="text-blue-600 hover:underline font-bold text-[10px]"
                >
                  Show All
                </button>
              )}
              <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xs font-bold">
                Close ✕
              </button>
            </div>
          </div>

          {matches.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400 font-medium">
              No matching shade found for "{query}"
            </div>
          ) : (
            <>
              {visible.map((shade, index) => (
                <button
                  key={`${shade.code}-${shade.hex}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onPick(shade);
                    updateQuery('');
                    onClose();
                  }}
                  className="w-full p-2 rounded-lg hover:bg-blue-50/80 text-left transition-colors flex items-center justify-between group border border-transparent hover:border-blue-200"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <ColorSwatch hex={shade.hex} className="w-5 h-5" />
                    <div className="truncate">
                      <span className="text-xs font-bold text-slate-800 group-hover:text-blue-900 block truncate">
                        {shade.name}
                      </span>
                      {shade.code && (
                        <span className="text-[10px] font-mono font-bold text-blue-600 block">Code: {shade.code}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-slate-500 group-hover:text-blue-700">
                    {shade.hex}
                  </span>
                </button>
              ))}

              {matches.length > limit && (
                <div className="p-2 text-center border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setLimit((current) => current + MORE)}
                    className="w-full py-1.5 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold rounded-lg transition-colors border border-blue-200"
                  >
                    Load More Shades (Showing {visible.length} of {matches.length.toLocaleString()})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
